import { useEffect, useState } from 'react';
import {
  RiUserAddLine,
  RiDeleteBin6Line,
  RiShieldUserLine,
  RiRefreshLine,
  RiVipCrownLine,
  RiArrowUpCircleLine,
  RiPencilLine,
  RiEyeLine,
} from 'react-icons/ri';
import type { IconType } from 'react-icons';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { api } from '../state/api';
import { useStore } from '../state/store';
import { useAuthStore } from '../state/authStore';
import { CASE_ROLE_LABEL, type CaseMember, type CaseRole } from '../state/domain';
import { cn, formatDay, initials } from '@/lib/utils';

const ROLE_META: Record<CaseRole, { icon: IconType; variant: 'default' | 'muted' | 'evidence' }> = {
  owner: { icon: RiVipCrownLine, variant: 'default' },
  editor: { icon: RiPencilLine, variant: 'evidence' },
  viewer: { icon: RiEyeLine, variant: 'muted' },
};

export function MembersDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const engagementId = useStore(state => state.activeEngagementId);
  const engagement = useStore(state => state.engagements.find(e => e.id === state.activeEngagementId));
  const toast = useStore(state => state.toast);
  const currentUser = useAuthStore(state => state.user);

  const [items, setItems] = useState<CaseMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer'>('editor');
  const [provision, setProvision] = useState({ name: '', username: '', password: '' });

  const isOwner = engagement?.myRole === 'owner' || currentUser?.role === 'admin';

  const refresh = async () => {
    if (!engagementId) return;
    setLoading(true);
    try {
      const { items: list } = await api.listMembers(engagementId);
      setItems(list);
    } catch (error) {
      toast('error', (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, engagementId]);

  const invite = async () => {
    if (!engagementId || !email.trim()) return;
    try {
      const { items: list } = await api.addMember({
        engagementId,
        email: email.trim(),
        role,
        name: provision.name.trim() || undefined,
        username: provision.username.trim() || undefined,
        password: provision.password || undefined,
      });
      setItems(list);
      setEmail('');
      setProvision({ name: '', username: '', password: '' });
      toast('success', '成员已加入案件');
      await useStore.getState().refreshAudit();
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const changeRole = async (userId: string, next: 'editor' | 'viewer') => {
    if (!engagementId) return;
    try {
      const { items: list } = await api.updateMember(userId, engagementId, next);
      setItems(list);
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const transfer = async (userId: string, name: string) => {
    if (!engagementId) return;
    if (!window.confirm(`将「${name}」设为案件负责人？原负责人的角色会降为编辑。`)) return;
    try {
      const { items: list } = await api.transferOwner(engagementId, userId);
      setItems(list);
      await useStore.getState().refreshTargets();
      await useStore.getState().refreshAudit();
      toast('success', '负责人已变更');
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  const remove = async (userId: string) => {
    if (!engagementId) return;
    try {
      const { items: list } = await api.removeMember(userId, engagementId);
      setItems(list);
      toast('success', '成员已移出案件');
    } catch (error) {
      toast('error', (error as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent className="w-[min(640px,95vw)]">
        <DialogHeader>
          <DialogTitle>团队成员</DialogTitle>
          <DialogDescription>
            成员可直接访问「{engagement?.name ?? '当前案件'}」，无需共享链接。
            <strong className="text-foreground"> 编辑</strong> 可增删情报与画布，
            <strong className="text-foreground"> 只读</strong> 仅能查看。
          </DialogDescription>
        </DialogHeader>

        {isOwner ? (
          <div className="grid grid-cols-[1fr_auto_auto] items-end gap-2">
            <div className="flex flex-col gap-1.5">
              <Label>按邮箱邀请（对方需已注册）</Label>
              <Input
                value={email}
                placeholder="analyst@example.com"
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && invite()}
              />
            </div>
            <div className="flex w-[104px] flex-col gap-1.5">
              <Label>角色</Label>
              <Select value={role} onValueChange={value => setRole(value as 'editor' | 'viewer')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">编辑</SelectItem>
                  <SelectItem value="viewer">只读</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={invite} disabled={!email.trim()}>
              <RiUserAddLine className="size-3.5" />
              邀请
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-[var(--radius)] border border-border bg-muted/40 p-3 text-[11px] text-muted-foreground">
            <RiShieldUserLine className="size-3.5 shrink-0" />
            仅案件负责人可以管理成员。你当前的角色是
            <Badge variant="outline">{engagement?.myRole ? CASE_ROLE_LABEL[engagement.myRole] : '—'}</Badge>
          </div>
        )}

        {isOwner && (
          <div className="mt-2 rounded-[var(--radius)] border border-border bg-muted/40 p-2.5">
            <div className="mb-1.5 text-[10.5px] text-muted-foreground">
              {currentUser?.role === 'admin'
                ? '对方尚无账号时可在此直接开户（留空则仅邀请已有账号）'
                : '仅平台管理员可为新成员开户；其他情况请先请管理员创建账号'}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input
                className="h-7 text-[11px]"
                placeholder="姓名"
                value={provision.name}
                onChange={e => setProvision({ ...provision, name: e.target.value })}
              />
              <Input
                className="h-7 text-[11px]"
                placeholder="用户名（可留空）"
                value={provision.username}
                onChange={e => setProvision({ ...provision, username: e.target.value })}
              />
              <Input
                className="h-7 text-[11px]"
                placeholder="初始口令"
                value={provision.password}
                onChange={e => setProvision({ ...provision, password: e.target.value })}
              />
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <span className="label-caps">成员（{items.length}）</span>
          <Button size="xs" variant="ghost" onClick={refresh} disabled={loading}>
            <RiRefreshLine className={cn('size-3', loading && 'animate-spin')} />
            刷新
          </Button>
        </div>

        <div className="mt-2 flex flex-col gap-1.5">
          {items.map(member => {
            const meta = ROLE_META[member.role];
            const Icon = meta.icon;
            const isSelf = member.userId === currentUser?.id;
            return (
              <div
                key={member.userId}
                className="flex items-center gap-2.5 rounded-[var(--radius)] border border-border bg-card p-2.5"
              >
                <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-muted text-[11px] font-semibold">
                  {member.avatar ? (
                    <img src={member.avatar} alt="" className="size-full object-cover" />
                  ) : (
                    initials(member.name)
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-[12px] font-medium">{member.name}</span>
                    {isSelf && <Badge variant="outline">你</Badge>}
                    <Badge variant={meta.variant} className="gap-1">
                      <Icon className="size-3" />
                      {member.roleLabel}
                    </Badge>
                  </div>
                  <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">
                    {member.email} · 加入 {formatDay(member.joinedAt)}
                  </div>
                </div>

                {isOwner && member.role !== 'owner' && (
                  <>
                    <Select
                      value={member.role}
                      onValueChange={value => changeRole(member.userId, value as 'editor' | 'viewer')}
                    >
                      <SelectTrigger className="h-7 w-[86px] shrink-0 text-[11px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="editor">编辑</SelectItem>
                        <SelectItem value="viewer">只读</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      title="设为负责人"
                      className="shrink-0 text-muted-foreground hover:text-primary"
                      onClick={() => transfer(member.userId, member.name)}
                    >
                      <RiArrowUpCircleLine className="size-3.5" />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      title="移出案件"
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => remove(member.userId)}
                    >
                      <RiDeleteBin6Line className="size-3.5" />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="rounded-[var(--radius)] border border-dashed border-border p-5 text-center text-[11.5px] text-muted-foreground">
              暂无成员信息。
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}